// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-MAP-1 — the view builder's control over the mapped-column backfill.
//
// A column mapped to a PioneerRx path fills for NEW prescriptions as they arrive. Existing ones
// fill only when a backfill runs. This panel runs one now, shows its progress, and sets the
// schedule that runs it automatically inside the pharmacy's working hours. The work happens on the
// server: closing this page does not stop it.

import { useEffect, useState } from 'react'
import { useCancelMappingBackfill, useMappingBackfill, useSaveMappingBackfillSettings, useStartMappingBackfill } from '../../hooks/queries/mappingBackfill'
import { friendlyError } from '../../api/errors'
import { formatUsDateTime } from '../../api/format'
import type { MappingBackfillRun, MappingBackfillSettings } from '../../api/client'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const TRIGGER_WORDS: Record<MappingBackfillRun['trigger'], string> = {
  manual: 'Run now', save: 'after a mapping change', auto: 'automatic',
}

const hour = (h: number) => (h === 0 || h === 24 ? '12 AM' : h === 12 ? '12 PM' : h < 12 ? `${h} AM` : `${h - 12} PM`)

/** Why a run is not moving, in words. The job never wakes a sleeping database and backs off a busy one. */
const WAITING: Record<NonNullable<MappingBackfillRun['wait_reason']>, (d: string | null | undefined) => string> = {
  db_asleep: () => "Waiting: the pharmacy's database is asleep. The backfill never wakes it; it continues the next time normal use wakes it.",
  db_state_unknown: () => "Waiting: the database's state could not be read, so it is left alone. Checks again in 5 minutes.",
  db_busy: d => `Waiting: the database is busy${d ? ` (${d})` : ''}. Users come first; checks again in 5 minutes.`,
  db_slow: d => `Paused: the database answered slowly${d ? ` (${d})` : ''}. Tries again in 5 minutes.`,
  outside_hours: () => 'Paused outside working hours. Resumes next working morning.',
}

/** One line that says where a run is. */
export function describeRun(r: MappingBackfillRun, inHours: boolean | undefined): string {
  const total = r.rx_total ?? 0
  const pct = total > 0 ? Math.min(100, Math.floor((r.rx_scanned / total) * 100)) : 0
  const of = total > 0 ? ` of ${total.toLocaleString()}` : ''
  if ((r.status === 'queued' || r.status === 'running') && r.wait_reason && r.wait_reason !== 'outside_hours') {
    const done = r.rx_scanned > 0 ? ` ${r.rx_scanned.toLocaleString()}${of} prescriptions done (${pct}%).` : ''
    return WAITING[r.wait_reason](r.wait_detail) + done
  }
  switch (r.status) {
    case 'queued':
      return r.trigger === 'auto' && inHours === false
        ? 'Queued. Starts at the beginning of the next working hours.'
        : 'Queued. Starts within 5 minutes.'
    case 'running':
      return r.trigger === 'auto' && inHours === false
        ? `Paused outside working hours at ${r.rx_scanned.toLocaleString()}${of} prescriptions (${pct}%). Resumes next working morning.`
        : `Backfilling… ${r.rx_scanned.toLocaleString()}${of} prescriptions (${pct}%), ${r.values_written.toLocaleString()} values written.`
    case 'done':
      return `Done ${formatUsDateTime(r.finished_at)}: ${r.rx_scanned.toLocaleString()} prescriptions, ${r.values_written.toLocaleString()} values written${r.errors ? `, ${r.errors} errors` : ''} (${TRIGGER_WORDS[r.trigger]}).`
    case 'failed':
      return `Failed ${formatUsDateTime(r.finished_at)} after ${r.rx_scanned.toLocaleString()}${of} prescriptions: ${r.first_error ?? 'unknown error'}`
    case 'cancelled':
      return `Stopped ${formatUsDateTime(r.finished_at)} at ${r.rx_scanned.toLocaleString()}${of} prescriptions.`
  }
}

export default function MappingBackfillPanel({ pharmacyId }: { pharmacyId: string | undefined }) {
  const status = useMappingBackfill(pharmacyId)
  const start = useStartMappingBackfill(pharmacyId)
  const cancel = useCancelMappingBackfill(pharmacyId)
  const save = useSaveMappingBackfillSettings(pharmacyId)
  const [draft, setDraft] = useState<MappingBackfillSettings | null>(null)
  const [editing, setEditing] = useState(false)

  const settings = status.data?.settings
  useEffect(() => { if (settings && !editing) setDraft(settings) }, [settings, editing])

  const active = status.data?.active ?? null
  const last = status.data?.last_completed ?? null
  const shown = active ?? status.data?.runs?.[0] ?? null
  const busy = start.isPending || cancel.isPending
  const error = start.error ?? cancel.error ?? save.error ?? status.error

  const dirty = !!draft && !!settings && (draft.auto_enabled !== settings.auto_enabled || draft.time_zone !== settings.time_zone
    || draft.start_hour !== settings.start_hour || draft.end_hour !== settings.end_hour || draft.work_days !== settings.work_days
    || draft.cpu_ceiling_pct !== settings.cpu_ceiling_pct || draft.duty_pct !== settings.duty_pct)
  const setDay = (i: number, on: boolean) => draft &&
    setDraft({ ...draft, work_days: draft.work_days.slice(0, i) + (on ? '1' : '0') + draft.work_days.slice(i + 1) })

  return (
    <section style={panel} aria-label="Mapped column backfill">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>Backfill mapped columns</strong>
        <button type="button" style={btn} disabled={busy || !!active} onClick={() => start.mutate('manual')}
                title="Fill every PioneerRx-mapped column for every existing prescription. Runs on the server; you can leave this page.">
          {start.isPending ? 'Starting…' : active ? 'Running' : 'Run now'}
        </button>
        {active && (
          <button type="button" style={btn} disabled={busy} onClick={() => cancel.mutate()}>Stop</button>
        )}
        <span style={muted} data-testid="backfill-status">
          {status.isLoading ? 'Loading…' : shown ? describeRun(shown, settings?.in_working_hours) : 'Never run.'}
        </span>
      </div>
      {active && last && (
        <div style={{ ...muted, marginTop: 4 }}>Last completed: {describeRun(last, true)}</div>
      )}

      {draft && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          <label style={row}>
            <input type="checkbox" checked={draft.auto_enabled}
                   onChange={e => { setEditing(true); setDraft({ ...draft, auto_enabled: e.target.checked }) }} />
            Run automatically during working hours
          </label>
          <label style={row}>from
            <input type="number" min={0} max={23} value={draft.start_hour} style={num} aria-label="Start hour (0-23)"
                   onChange={e => { setEditing(true); setDraft({ ...draft, start_hour: Number(e.target.value) }) }} />
          </label>
          <label style={row}>to
            <input type="number" min={1} max={24} value={draft.end_hour} style={num} aria-label="End hour (1-24)"
                   onChange={e => { setEditing(true); setDraft({ ...draft, end_hour: Number(e.target.value) }) }} />
          </label>
          <span style={muted}>({hour(draft.start_hour)} to {hour(draft.end_hour)})</span>
          <span style={row}>
            {DAYS.map((d, i) => (
              <label key={d} style={{ ...row, gap: 2 }}>
                <input type="checkbox" checked={draft.work_days[i] === '1'}
                       onChange={e => { setEditing(true); setDay(i, e.target.checked) }} />{d}
              </label>
            ))}
          </span>
          <label style={row}>time zone
            <input type="text" value={draft.time_zone} style={{ ...num, width: 170 }} aria-label="Time zone"
                   onChange={e => { setEditing(true); setDraft({ ...draft, time_zone: e.target.value }) }} />
          </label>
          <label style={row} title="The database's own CPU, worker and log use, read every few slices. Above this the backfill stops and waits for the next check.">
            back off above
            <input type="number" min={10} max={90} value={draft.cpu_ceiling_pct} style={num} aria-label="Back off above database CPU percent"
                   onChange={e => { setEditing(true); setDraft({ ...draft, cpu_ceiling_pct: Number(e.target.value) }) }} />% database CPU
          </label>
          <label style={row} title="The backfill rests between slices so it works only this share of the time.">
            work at most
            <input type="number" min={10} max={90} value={draft.duty_pct} style={num} aria-label="Work at most percent of the time"
                   onChange={e => { setEditing(true); setDraft({ ...draft, duty_pct: Number(e.target.value) }) }} />% of the time
          </label>
          <button type="button" style={btn} disabled={!dirty || save.isPending}
                  onClick={() => save.mutate(draft, { onSuccess: () => setEditing(false) })}>
            {save.isPending ? 'Saving…' : 'Save schedule'}
          </button>
          {settings?.auto_enabled && (
            <span style={muted}>{settings.in_working_hours ? 'Inside working hours now.' : 'Outside working hours now.'}</span>
          )}
        </div>
      )}
      <div style={{ ...muted, marginTop: 6 }}>
        Never wakes a sleeping database: it only works while the database is already awake, one slice at a time, and stops when the database is busy.
      </div>
      {error && <div style={{ color: '#B23A22', fontSize: 12, marginTop: 6 }}>{friendlyError(error)}</div>}
    </section>
  )
}

const panel: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 8, padding: '10px 12px', margin: '8px 0 12px', background: 'var(--color-surface, #fff)' }
const btn: React.CSSProperties = { border: '1px solid var(--color-border)', background: 'var(--color-bg)', borderRadius: 7, padding: '5px 10px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const muted: React.CSSProperties = { fontSize: 12.5, color: 'var(--color-text-soft)' }
const row: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5 }
const num: React.CSSProperties = { width: 56, padding: '3px 6px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit' }
